"use client";

import * as React from "react";
import { useCallback, useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import ConditionalTakeQuizButtonContainer from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/conditional-take-quiz-button.container";
import ClearFiltersContainer from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/clear-filters.container";
import {
  FILTER_KEYS,
  FilterValues,
} from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/filter-schema";
import SavedSearchesAndPreferences from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/saved-search-and-preferences/saved-searches-and-preferences";
import SearchFiltersView from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/search-filters.view";
import SearchFiltersTitle from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/search-filters-title";
import useActiveFilterCount from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-active-filter-count";
import useFiltersForm from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-filters-form";
import useResetFilters from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-reset-filters";
import useUrlState from "@/app/(discover)/jobs/[stateName]/(state-jobs)/components/filters/use-url-state";
import { TakeQuizButton } from "@/components/shared/buttons/take-quiz-button";
import useStateConfig from "@/hooks/use-state-config";
import { jobFiltersTestIds } from "@/lib/data-test-ids/jobs.data-test-ids";

export function SearchFiltersCardView({
  form,
  resetFilters,
  activeFilterCount,
  topContent,
  savedSearchesAndPreferences,
}: {
  form: UseFormReturn<FilterValues>;
  resetFilters: () => void;
  activeFilterCount: number;
  topContent: React.ReactNode;
  savedSearchesAndPreferences: React.ReactNode;
}) {
  return (
    <div className="hidden lg:block w-70 flex-shrink-0">
      <div className="flex flex-col gap-2.5">
        <ConditionalTakeQuizButtonContainer
          button={
            <TakeQuizButton
              dataTestId={jobFiltersTestIds.TAKE_QUIZ_BUTTON}
              className="w-full h-9 text-sm py-4 flex-shrink-0"
            />
          }
        />

        {topContent}

        <div data-testid="search-filters" className="flex flex-col">
          {savedSearchesAndPreferences}
          <div className="flex items-center justify-between border-b border-border pb-3">
            <SearchFiltersTitle />
            <ClearFiltersContainer
              resetFilters={resetFilters}
              activeFilterCount={activeFilterCount}
            />
          </div>
          <div className="pt-4 pb-4">
            <SearchFiltersView form={form} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SearchFiltersCardContainer({
  topContent,
}: {
  topContent?: React.ReactNode;
}) {
  const { onUrlValuesChange, urlValues } = useUrlState();

  const onFiltersChange = useCallback(
    (values: FilterValues) => {
      onUrlValuesChange(values);
    },
    [onUrlValuesChange],
  );

  const { form, filterDefaults } = useFiltersForm({
    overrideValues: urlValues,
  });

  const { supportsSavedSearches } = useStateConfig();

  const hasSavedSearchesAndPreferences = supportsSavedSearches;

  const activeFilterCount = useActiveFilterCount(
    form,
    FILTER_KEYS,
    filterDefaults,
  );

  const resetFilters = useResetFilters(form, filterDefaults);

  useEffect(() => {
    const subscription = form.watch(() => {
      onFiltersChange(form.getValues());
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [form, onFiltersChange]);

  const savedSearchesAndPreferences = hasSavedSearchesAndPreferences ? (
    <SavedSearchesAndPreferences form={form} filterDefaults={filterDefaults} />
  ) : null;

  return (
    <SearchFiltersCardView
      resetFilters={resetFilters}
      activeFilterCount={activeFilterCount}
      form={form}
      topContent={topContent}
      savedSearchesAndPreferences={savedSearchesAndPreferences}
    />
  );
}
